import { GridCellProps, GridColumn } from '@progress/kendo-react-grid';
import { Tooltip } from '@progress/kendo-react-tooltip';
import DownloadIcon from 'Assets/Icons/download.svg';
import { FilterBarMainGrid, ReportsFilesDownloadCell } from 'Components/Grid';
import {
  GridProvider,
  ModalFormProvider,
  useEntity,
  useGlobalContext,
  useGridState,
  useToasts,
} from 'Context';
import {
  ActionsDropdownConfig,
  AppointmentIdGridSearchModel,
  defaultGridProps,
  GridColumnWidth,
} from 'Models';
import {
  AppointmentReportModal,
  AppointmentReportModel,
  ReportTypeField,
} from 'Models/AppointmentReportModel';
import { Permission } from 'Models/Templates/Permission/Permission';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import {
  buildEndpoint,
  buildQueryUrl,
  DEFAULT_DATETIME_FORMAT,
  useApiWorker,
} from 'Utilities';
import download from 'downloadjs';
import { dayjs } from 'Utilities/DateTimeUtc';
import React, { Fragment } from 'react';
import { AppointmentReportDrawerForm } from '.';

export const AppointmentReportListClients: React.FC = () => {
  const entity = useEntity();

  return (
    <Fragment>
      <GridProvider entityName="Appointment Report">
        <CastAttachmentModalFormWrapper />
      </GridProvider>
    </Fragment>
  );
};

const CastAttachmentModalFormWrapper: React.FC = () => {
  const toast = useToasts();
  const entity = useEntity();
  const { refreshGrid, entityName } = useGridState();

  const handleSuccess = () => {
    toast.success(`${entityName} saved!`);
    refreshGrid();
  };

  var formModel = new AppointmentReportModel(
    entity.appointment?.id,
    entity.referralDate
  );

  return (
    <ModalFormProvider
      formModel={formModel}
      endpoint={ENDPOINTS.appointmentsreportsbundles.base}
      handleSuccess={handleSuccess}
    >
      <AppointmentReportGridWithModalDrawer />
    </ModalFormProvider>
  );
};

const AppointmentReportGridWithModalDrawer: React.FC = () => {
  const entity = useEntity();
  const status = entity.appointment?.appointmentStatus?.displayName;
  const isClosed = status === 'Closed';
  const globalContext = useGlobalContext();
  const userdata = globalContext[0]?.currentUser;
  const isClientTypeUser = userdata?.isClient;
  const searchModel: AppointmentIdGridSearchModel =
    new AppointmentIdGridSearchModel(entity.appointment?.id);

  var formModel = new AppointmentReportModel(
    entity.appointment?.id,
    entity.referralDate
  );

  const actionsConfig: ActionsDropdownConfig = {
    name: 'Actions',
    items: [
      {
        name: 'Upload Template',
        permissions: [Permission.UploadAppointmentReportTemplate],
        modalConfig: {
          modalId: AppointmentReportModal.AttachmentsReport,
          modalComponent: (
            <AppointmentReportDrawerForm
              endpoint={ENDPOINTS.appointmentsreportsbundles.base}
              formModel={formModel}
              modal={AppointmentReportModal.AttachmentsReport}
              attachmentType="Template"
            />
          ),
        },
      },
    ],
  };

  return (
    <Fragment>
      <div style={{ marginTop: 20 }}>
        <Tooltip
          openDelay={100}
          position="bottom"
          anchorElement="target"
          className="tooltip-max-width"
        >
          <FilterBarMainGrid
            headerDisabled
            isMainGrid
            forceEmpty={!isClosed}
            headerTitle="Reports"
            noRecordsTitle={
              !isClosed ? 'Your case is still under review!' : undefined
            }
            noRecordsDescription={
              !isClosed
                ? 'Don’t worry, the finalized reports will appear here when they are ready'
                : undefined
            }
            filterBarProps={{
              filterConfigurations: [],
              filterValues: {},
              isColumnSelectorEnabled: false,
            }}
            readEndpoint={ENDPOINTS.appointmentsreportsbundles.read}
            enableGridRefresh={true}
            search={searchModel}
            {...defaultGridProps}
            pageSize={25}
            style={{ cursor: 'pointer' }}
          >
            <GridColumn
              field="reportFile"
              title="Report"
              cell={ReportsDownloadCell}
              width={GridColumnWidth.MedLarge}
            />
            {!isClientTypeUser && (
              <GridColumn
                field="templateFile"
                title="Other Document"
                cell={ReportsDownloadCell}
                width={GridColumnWidth.MedLarge}
              />
            )}

            <GridColumn
              field="reviewedBy"
              title="Reviewer"
              width={GridColumnWidth.SmallMed}
            />
            <GridColumn field="determination" title="Outcome" width="auto" />

            <GridColumn
              cell={DateAndTimeFormatCell}
              field="templateFileUploadDate"
              title="Date & Time Uploaded"
              width="auto"
            />

            <GridColumn
              cell={ActionsCell}
              field={ReportTypeField.TemplateField}
              title="Actions"
              sortable={false}
            />
          </FilterBarMainGrid>
        </Tooltip>
      </div>
    </Fragment>
  );
};

export const ActionsCell: React.FC<GridCellProps> = (props) => {
  const dataItem = props.dataItem;
  const toast = useToasts();
  const API = useApiWorker();
  const globalContext = useGlobalContext();
  const userdata = globalContext[0]?.currentUser;
  const isClientTypeUser = userdata?.isClient;

  const handleDownload = async () => {
    const filesToDownload = [
      {
        endpoint: ENDPOINTS.appointmentreports.base,
        file: dataItem.reportFile,
      },
    ];

    if (!isClientTypeUser) {
      filesToDownload.push({
        endpoint: ENDPOINTS.appointmentreportstemplates.base,
        file: dataItem.templateFile,
      });
    }

    for (const { endpoint, file } of filesToDownload) {
      if (!file?.id) continue;

      const downloadEndpoint = buildEndpoint(endpoint, 'download');
      const queryUrl = buildQueryUrl(downloadEndpoint, { id: file.id });

      try {
        const { data, headers } = await API.get(queryUrl, {
          responseType: 'blob',
        });

        const mimeType = headers['content-type'];
        const filename = file.fileName || 'download';

        if (mimeType === 'application/pdf') {
          const fileURL = URL.createObjectURL(data);
          window.open(fileURL, filename);
        } else {
          download(data, filename, mimeType);
        }
      } catch (err) {
        toast.error(`Failed to download ${file.fileName}`);
        console.error(err);
      }
    }
  };

  return (
    <td>
      <button
        type="button"
        onClick={handleDownload}
        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
      >
        <img src={DownloadIcon} alt="downloadIcon" />
      </button>
    </td>
  );
};
const DateAndTimeFormatCell: React.FC<GridCellProps> = (props) => {
  const dateValue = props.dataItem.templateFile?.uploadDate;
  // Parse UTC date from backend and display in local time with AM/PM
  const formatted = dateValue
    ? dayjs.utc(dateValue, 'YYYY-MM-DD HH:mm:ss').local().format(DEFAULT_DATETIME_FORMAT)
    : '';

  return <td>{formatted}</td>;
};
//Component to handle what kind of file is going to be downloaded
export const ReportsDownloadCell: React.FC<GridCellProps> = ({ ...props }) => {
  let endpoint =
    props.field == ReportTypeField.TemplateField
      ? ENDPOINTS.appointmentreportstemplates.base
      : ENDPOINTS.appointmentreports.base;
  return (
    <ReportsFilesDownloadCell fileControllerEndpoint={endpoint} {...props} />
  );
};
